import type { FC } from 'react'
import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import { useHistory, useLocation } from 'react-router-dom'

import {
  Typography,
  Select,
  PreviewDrawer,
  Table,
  Button,
  IconButton,
  Pagination,
  FloatingButton,
  DatePicker,
  Filters,
  ActivityLog,
  WindowTitle,
  SearchInput,
} from '@homeviews/shared-ui'
import { useGetReviewsQuery, useGetReviewsFiltersQuery } from '@homeviews/store'
import { getUser } from '@homeviews/store'
import { IS_COMPANY_GROUP_ADMIN } from '@homeviews/users'
import type { FilterType, Review } from '@homeviews/models'
import {
  ReviewCardSkeleton,
  ReviewCard,
  ReviewPreview,
  ReplyReviewForm,
  createRows,
  headCells,
  createSkeletonRows,
  SORT_OPTIONS,
} from '@homeviews/reviews'
import type { ITheme } from '@homeviews/styles'
import {
  setActiveReview,
  setFilter,
  setReviewSort,
  selectActiveReview,
  selectActiveSort,
  selectReviewsFilters,
  clearFilters,
} from '@homeviews/store'
import { transformFilterOptions, parseFiltersFromUrl } from '@homeviews/utils'
import { Drawer } from '@material-ui/core'
import { mixpanelTrack } from '@homeviews/seo'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      padding: theme.spacing(
        theme.layout.contentHorizontalPadding,
        theme.layout.contentVerticalPadding,
      ),
      '& .c-reviewsTable__reviewBodyCell': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        display: 'block',
      },
      '& .MuiTableCell-body': {
        fontSize: 14,
      },
    },
    reviewCardRoot: {
      marginBottom: theme.spacing(2.5),
    },
    selectWrapper: {
      flexDirection: 'column',
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(1.875 / 2),
      marginTop: theme.spacing(1.5 / 2),
      fontWeight: 500,
      color: theme.colors.grey800,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        '& .DateRangePicker': {
          width: '100%',
        },
        '& .DateRangePickerInput': {
          width: '100%',
          justifyContent: 'space-between',
        },
        '& .DateInput_fang': {
          display: 'none',
        },
      },
    },
    selectWrapperSort: {
      width: '115px',
    },
    selectWrapperSortDesktop: {
      '& .customSelect__value-container': {
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
    datePickerWrapper: {
      width: 'auto',
    },
    selectWrapperButton: {
      width: 'initial',
      flex: '0!important',
    },
    selectWrapperLast: {
      marginRight: 'auto',
    },
    filtersTitle: {
      color: theme.colors.grey600,
      fontWeight: 600,
      marginRight: 8,
      flex: '0!important',
    },
    filtersSummary: {
      color: theme.colors.grey600,
      fontWeight: 500,
      marginTop: theme.spacing(1.25),
      fontSize: '18px',
      letterSpacing: '-0.2px',
      lineHeight: '23px',
      [theme.breakpoints.down('sm')]: {
        marginBottom: 20,
      },
    },
    filtersSummaryMain: {
      display: 'block',
      '@media (min-width: 960px) and (max-width: 1850px)': {
        display: 'none',
      },
    },
    filterSummaryMediumScreen: {
      display: 'none',
      marginRight: 'auto',
      '@media (min-width: 960px) and (max-width: 1850px)': {
        display: 'block',
        marginTop: 0,
      },
    },
    filtersInner: {
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      '& > *': {
        flex: 1,
      },
      '@media (min-width: 960px) and (max-width: 1850px)': {
        marginBottom: 10,
        width: '100%',
      },
    },
    filtersOuter: {
      display: 'flex',
      width: '100%',
      '@media (min-width: 960px) and (max-width: 1850px)': {
        flexWrap: 'wrap',
      },
    },
    paginationWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(2.5),
      '@media (min-width: 960px) and (max-width: 1850px)': {
        marginLeft: 'auto',
        marginBottom: 10,
        width: '100%',
      },
    },
    paginationTopWrapper: {
      marginTop: 0,
    },
    viewButtons: {
      display: 'flex',
      marginLeft: 10,
    },
    mobileFiltersHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      '& h3': {
        marginRight: 'auto',
      },
    },
    mobileFiltersDrawer: {
      '& .MuiDrawer-paper': {
        padding: theme.spacing(3.75),
        overflow: 'initial',
      },
    },
    toggleViewButton: {
      opacity: 0.5,
    },
    toggleViewButtonActive: {
      opacity: 1,
    },
    activityLogWrapper: {
      marginTop: 10,
    },
    searchWrapper: {
      '& .c-searchInput': {
        '& .MuiTextField-root': {
          width: '100%',
          '& .MuiInputBase-root': {
            backgroundColor: theme.colors.white,
            fontWeight: 500,
          },
        },
      },
    },
  }),
)

const skeletonRows = createSkeletonRows()

const Reviews: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const activeReview = useSelector(selectActiveReview)
  const activeFilters = useSelector(selectReviewsFilters)
  const activeSort = useSelector(selectActiveSort)
  const user = useSelector(getUser)
  const isCompanyGroupAdmin = IS_COMPANY_GROUP_ADMIN(user)
  // CompanyGroupAdmin only: the company (developer) whose reviews are shown.
  // Chosen from the on-page company selector and defaults to the first
  // overseen company. Initialise it synchronously (protected routes only
  // mount once the user is resolved, so user.developers is already available)
  // so the first reviews/options request is scoped from the outset rather than
  // firing once unscoped and re-fetching. Reviews and the development filter
  // options are scoped to it, on top of the visible, user-clearable filters.
  const [selectedDeveloperId, setSelectedDeveloperId] = useState<number | null>(
    () => (isCompanyGroupAdmin ? user?.developers?.[0]?.id ?? null : null),
  )
  const developerOptions = useMemo(
    () =>
      (user?.developers ?? []).map((developer) => ({
        label: developer.name,
        value: developer.id,
      })),
    [user?.developers],
  )
  const developerScope = useMemo(
    () =>
      isCompanyGroupAdmin && selectedDeveloperId
        ? { 'developer[]': [selectedDeveloperId] }
        : undefined,
    [isCompanyGroupAdmin, selectedDeveloperId],
  )
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [listView, setListView] = useState<'cards' | 'table'>('table')

  const [mobileFiltersOpened, setMobileFiltersOpened] = useState<boolean>(false)
  const [loadReviewOnPageChange, setLoadReviewOnPageChange] = useState<
    'none' | 'first' | 'last'
  >('none')

  const { data: response, isFetching } = useGetReviewsQuery({
    ...activeFilters,
    ...activeSort,
    ...developerScope,
  })
  const { data: devOptionsResponse } = useGetReviewsFiltersQuery(
    isCompanyGroupAdmin && selectedDeveloperId
      ? { field: 'development', developer: [selectedDeveloperId] }
      : 'development',
  )
  const { data: reviewerTypeResponse } =
    useGetReviewsFiltersQuery('reviewer_type')
  const { data: reviewerResponseOptionsResponse } =
    useGetReviewsFiltersQuery('response')

  const devOptions = useMemo(
    () => transformFilterOptions(devOptionsResponse),
    [devOptionsResponse],
  )
  const reviewerTypeOptions = useMemo(
    () => transformFilterOptions(reviewerTypeResponse),
    [reviewerTypeResponse],
  )
  const reviewResponseOptions = useMemo(
    () => transformFilterOptions(reviewerResponseOptionsResponse),
    [reviewerResponseOptionsResponse],
  )

  const reviews = response?.data || []
  const meta = response?.meta

  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    // If review id is set open panel
    if (activeFilters?.review) {
      const newReview = reviews.find(
        (review: Review) => review.id === Number(activeFilters.review),
      )

      if (newReview) {
        dispatch(
          setActiveReview({
            review: newReview,
          }),
        )
        mixpanelTrack('REVIEWS_SELECT_FULL_REVIEW', {
          'Review Title': newReview.title,
        })
      }
    }
  }, [response])

  useEffect(() => {
    if (!activeReview) {
      return
    }
    let newActiveReview = null
    if (loadReviewOnPageChange === 'first') {
      newActiveReview = reviews[0]
    } else if (loadReviewOnPageChange === 'last') {
      newActiveReview = reviews[reviews.length - 1]
    }
    newActiveReview &&
      dispatch(
        setActiveReview({
          review: newActiveReview,
        }),
      )
  }, [meta?.current_page])

  useEffect(() => {
    const filtersFromUrl = parseFiltersFromUrl()

    if (filtersFromUrl?.length) {
      filtersFromUrl.forEach((el: any) => {
        dispatch(setFilter(el))
      })
    }
  }, [])

  const handleClick = (id: number) => {
    const newReview = reviews.find((review: Review) => review.id === id)

    if (newReview) {
      dispatch(
        setActiveReview({
          review: newReview,
        }),
      )

      mixpanelTrack('REVIEWS_SELECT_FULL_REVIEW', {
        'Review Title': newReview.title,
      })

      // Set review query param
      const params = new URLSearchParams()
      params.append('review', String(newReview.id))
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      })
    }
  }

  const handleDrawerClose = () => {
    setLoadReviewOnPageChange('none')
    dispatch(
      setActiveReview({
        review: null,
      }),
    )
    history.replace({ pathname: location.pathname })
  }

  const handleSelectFilterChange = (filterType: FilterType) => (option) => {
    dispatch(
      setFilter({
        filter: filterType,
        value: option?.value || option?.value === 0 ? option.value : false,
      }),
    )

    if (filterType === 'reviewer_type') {
      mixpanelTrack('REVIEWS_REVIEWER_TYPE_FILTER', {
        'Reviewer Type': option?.label,
      })
    } else if (filterType === 'response') {
      mixpanelTrack('REVIEWS_RESPONSE_TYPE_FILTER', {
        'Response Type': option?.label,
      })
    } else if (filterType === 'verified') {
      mixpanelTrack('REVIEWS_VERIFIED_FILTER', {
        'Verification Type': option?.label,
      })
    }
  }

  const handleDeveloperChange = (option: { value: number }) => {
    if (!option?.value) {
      return
    }
    setSelectedDeveloperId(option.value)
    // Development options are scoped per company, so any development selected
    // for the previous company no longer applies.
    dispatch(setFilter({ filter: 'development[]', value: [] }))
    mixpanelTrack('COMPANY_GROUP_ADMIN_DEVELOPER_CHANGE', {
      'Developer Id': option.value,
    })
  }

  const handlePerPageChange = (page: number) => {
    dispatch(
      setFilter({
        filter: 'posts_per_page',
        value: page,
      }),
    )
  }

  const handlePageChange = (page: number) => {
    dispatch(
      setFilter({
        filter: 'page',
        value: page,
      }),
    )
  }

  const handleClearFilters = () => {
    dispatch(clearFilters())
    mixpanelTrack('REVIEWS_RESET_FILTERS')

    history.replace({
      pathname: location.pathname,
      search: '',
    })
  }

  const handleSortChange = (sort: any) => {
    mixpanelTrack('REVIEWS_SORT', {
      'Sort by': sort?.label || '',
    })

    if (sort?.forceValue) {
      dispatch(setReviewSort({ sort: sort?.forceValue }))
      return
    }
    dispatch(setReviewSort({ sort }))
  }

  const switchReview = (direction: 'prev' | 'next') => {
    const id = activeReview?.id
    if (!id) return

    const activeReviewIndex = reviews.findIndex(
      (review: Review) => review.id === id,
    )

    const prev = reviews[activeReviewIndex - 1]
    const next = reviews[activeReviewIndex + 1]
    const current = reviews[activeReviewIndex]

    let newReview: Review = null
    if (!direction) {
      newReview = current
    } else if (direction === 'prev' && prev) {
      newReview = prev
    } else if (direction === 'next' && next) {
      newReview = next
    }

    if (!newReview) {
      if (direction === 'prev') {
        setLoadReviewOnPageChange('last')
        handlePageChange(meta.current_page - 1)
      } else if (direction === 'next') {
        setLoadReviewOnPageChange('first')
        handlePageChange(meta.current_page + 1)
      }
      return
    }

    dispatch(
      setActiveReview({
        review: newReview,
      }),
    )
  }

  let nextReviewDisabled = false
  let prevReviewDisabled = false

  if (!!reviews.length && activeReview?.id) {
    const activeReviewIndex = reviews.findIndex(
      (review: Review) => review.id === activeReview?.id,
    )
    if (activeReviewIndex || activeReviewIndex === 0) {
      if (meta?.current_page === 1 && activeReviewIndex === 0) {
        prevReviewDisabled = true
      }
      if (
        meta?.current_page === meta?.last_page &&
        activeReviewIndex === reviews.length - 1
      ) {
        nextReviewDisabled = true
      }
    }
  }

  const handleDateChange = (newData: {
    date_from: string
    date_to: string
  }) => {
    dispatch(
      setFilter({
        filter: 'date_from',
        value: newData?.date_from,
      }),
    )
    dispatch(
      setFilter({
        filter: 'date_to',
        value: newData?.date_to,
      }),
    )

    if (
      activeFilters.date_from !== newData?.date_from ||
      activeFilters.date_to !== newData?.date_to
    ) {
      mixpanelTrack('REVIEWS_DATE_FILTER', {
        'From Date': newData?.date_from,
        'To Date': newData?.date_to,
      })
    }
  }

  const handleMultiSelectFilterChange = useCallback(
    (filterType: FilterType) => (option) => {
      const newOptions = []
      const labels = []
      if (option) {
        option.forEach((x) => {
          if (x.value) {
            newOptions.push(x.value)
            labels.push(x.label)
          }
        })
      }

      dispatch(
        setFilter({
          filter: filterType,
          value: newOptions,
        }),
      )

      if (filterType === 'development[]') {
        mixpanelTrack('REVIEWS_DEVELOPMENT_FILTER', {
          'Development Name': labels,
        })
      }
    },
    [dispatch],
  )

  const handleSearchChange = (value: any) => {
    dispatch(
      setFilter({
        filter: 'keyword',
        value,
      }),
    )

    mixpanelTrack('REVIEWS_SEARCH_FILTER', {
      'Search Term': value,
    })
  }

  const rows = useMemo(
    () => (listView === 'cards' ? [] : createRows(reviews)),
    [reviews, listView],
  )

  const defaultPerPage = 20

  const renderFilters = () => (
    <>
      {isCompanyGroupAdmin && developerOptions.length > 0 && (
        <div className={classes.selectWrapper}>
          <Select
            placeholder="Select company"
            options={developerOptions}
            value={selectedDeveloperId ?? undefined}
            onChange={handleDeveloperChange}
          />
        </div>
      )}
      <div className={clsx(classes.selectWrapper, classes.searchWrapper)}>
        <SearchInput
          value={activeFilters?.['keyword'] || ''}
          onChange={handleSearchChange}
          className="c-searchInput"
        />
      </div>
      <div className={classes.selectWrapper}>
        <Select
          placeholder="Select Developments"
          options={devOptions}
          onChange={handleMultiSelectFilterChange('development[]')}
          value={activeFilters?.['development[]']}
          withCheckboxes
          isMulti
          isClearable
        />
      </div>
      <div className={classes.selectWrapper}>
        <Select
          placeholder="Select Reviewer Type"
          options={reviewerTypeOptions}
          onChange={handleSelectFilterChange('reviewer_type')}
          value={activeFilters?.['reviewer_type']}
          isClearable
        />
      </div>
      <div className={clsx(classes.selectWrapper, classes.datePickerWrapper)}>
        <DatePicker
          initialStartDate={activeFilters?.['date_from'] || null}
          initialEndDate={activeFilters?.['date_to'] || null}
          onChange={handleDateChange}
          {...(isMobile && {
            verticalHeight: 370,
            orientation: 'vertical',
            openDirection: 'top',
            anchorDirection: 'bottom',
          })}
        />
      </div>
      <div className={classes.selectWrapper}>
        <Select
          placeholder="Response type"
          options={reviewResponseOptions}
          onChange={handleSelectFilterChange('response')}
          value={activeFilters?.['response']}
          isClearable
        />
      </div>
      <div className={classes.selectWrapper}>
        <Select
          placeholder="Verification Status"
          options={[
            { value: 1, label: 'Verified' },
            { value: 0, label: 'Unverified' },
          ]}
          onChange={handleSelectFilterChange('verified')}
          value={activeFilters?.['verified']}
          isClearable
        />
      </div>
    </>
  )

  return (
    <>
      <WindowTitle title="Reviews" />
      <div className={classes.root}>
        <Typography variant="h1">Reviews</Typography>
        {!!meta?.total && !!reviews?.length && (
          <Typography
            className={clsx(classes.filtersSummary, classes.filtersSummaryMain)}
          >{`${reviews.length} ${
            reviews.length === 1 ? 'review' : 'reviews'
          } shown of ${meta.total}`}</Typography>
        )}
        {!isMobile && (
          <Filters isLoading={isFetching} innerClassName={classes.filtersOuter}>
            <div className={classes.filtersInner}>
              <Typography className={classes.filtersTitle}>Filters</Typography>
              {renderFilters()}
              <div
                className={clsx(
                  classes.selectWrapper,
                  classes.selectWrapperButton,
                )}
              >
                <Button
                  variant="outlined"
                  customColor="dark"
                  onClick={handleClearFilters}
                >
                  Reset filters
                </Button>
              </div>
              <div
                className={clsx(
                  classes.selectWrapper,
                  classes.selectWrapperSortDesktop,
                )}
              >
                <Select
                  placeholder="Sort by"
                  options={SORT_OPTIONS}
                  value={
                    activeSort?.sort
                      ? `${activeSort?.sort}_${activeSort?.sort_direction}`
                      : null
                  }
                  customVariant="transparent"
                  onChange={handleSortChange}
                />
              </div>
            </div>
            <div
              className={clsx(
                classes.paginationWrapper,
                classes.paginationTopWrapper,
              )}
            >
              {!!meta?.total && !!reviews?.length && (
                <Typography
                  className={clsx(
                    classes.filtersSummary,
                    classes.filterSummaryMediumScreen,
                  )}
                >{`${reviews.length} ${
                  reviews.length === 1 ? 'review' : 'reviews'
                } shown of ${meta.total}`}</Typography>
              )}
              <Pagination
                currentPage={meta?.current_page}
                lastPage={meta?.last_page}
                perPage={activeFilters?.['posts_per_page'] || defaultPerPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
              />
              <div className={classes.viewButtons}>
                <IconButton
                  svgIcon="medium/grid"
                  svgSource="image"
                  variant="transparent"
                  onClick={() => {
                    setListView('cards')
                    mixpanelTrack('REVIEWS_SELECT_VIEW', { View: 'Cards' })
                  }}
                  className={clsx(
                    classes.toggleViewButton,
                    listView === 'cards' && classes.toggleViewButtonActive,
                  )}
                />
                <IconButton
                  svgIcon="medium/list"
                  svgSource="image"
                  variant="transparent"
                  onClick={() => {
                    setListView('table')
                    mixpanelTrack('REVIEWS_SELECT_VIEW', { View: 'List' })
                  }}
                  className={clsx(
                    classes.toggleViewButton,
                    listView === 'table' && classes.toggleViewButtonActive,
                  )}
                />
              </div>
            </div>
          </Filters>
        )}
        {isFetching &&
          listView === 'cards' &&
          Array(10)
            .fill('0')
            .map((i, index) => <ReviewCardSkeleton key={index} />)}
        {!isFetching &&
          !!reviews?.length &&
          listView === 'cards' &&
          reviews.map((review: any) => {
            const { id, verified } = review
            return (
              <ReviewCard
                key={id}
                {...review}
                verified={verified}
                className={classes.reviewCardRoot}
                onClick={handleClick}
                searchKeyword={activeFilters?.['keyword']}
              />
            )
          })}
        {listView === 'table' && (
          <Table
            headCells={headCells}
            rows={isFetching ? skeletonRows : rows}
            onRowClick={handleClick}
            onSortChange={handleSortChange}
            activeSort={activeSort}
          />
        )}
        {!reviews?.length && !isFetching && (
          <div className="l-noRecords">No Reviews</div>
        )}
        <div className={classes.paginationWrapper}>
          <Pagination
            currentPage={meta?.current_page}
            lastPage={meta?.last_page}
            perPage={activeFilters?.['posts_per_page'] || defaultPerPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </div>
        <PreviewDrawer
          open={!!activeReview}
          onClose={handleDrawerClose}
          currentPage={meta?.current_page}
          lastPage={meta?.last_page}
          navigate={switchReview}
          prevDisabled={prevReviewDisabled}
          nextDisabled={nextReviewDisabled}
        >
          {!!activeReview && (
            <>
              <ReviewPreview review={activeReview} />
              <ReplyReviewForm
                review={activeReview}
                handleCancelForm={handleDrawerClose}
              />
              <div className={classes.activityLogWrapper}>
                <ActivityLog recordType="review" />
              </div>
            </>
          )}
        </PreviewDrawer>

        {mobileFiltersOpened && (
          <Drawer
            open={true}
            anchor="bottom"
            onClose={() => setMobileFiltersOpened(false)}
            className={classes.mobileFiltersDrawer}
          >
            <div className={classes.mobileFiltersHeader}>
              <Typography variant="h3">Filters</Typography>
              <div
                className={clsx(
                  classes.selectWrapper,
                  classes.selectWrapperSort,
                )}
              >
                <Select
                  placeholder="Sort by"
                  options={SORT_OPTIONS}
                  value={`${activeSort?.sort}_${activeSort?.sort_direction}`}
                  customVariant="transparent"
                  onChange={handleSortChange}
                />
              </div>
              <Button
                variant="outlined"
                customColor="dark"
                onClick={handleClearFilters}
              >
                Reset filters
              </Button>
            </div>
            {renderFilters()}
          </Drawer>
        )}

        {isMobile && (
          <FloatingButton
            svgIcon="filters"
            variant="extended"
            color="primary"
            onClick={() => setMobileFiltersOpened(true)}
          >
            Filters
          </FloatingButton>
        )}
      </div>
    </>
  )
}

export default Reviews
